export enum FieldTypes {
  INTEGER,
  DECIMAL,
  BOOLEAN,
  SELECT,
}

type FieldType =
  | { type: FieldTypes.INTEGER }
  | { type: FieldTypes.DECIMAL }
  | { type: FieldTypes.BOOLEAN }
  | { type: FieldTypes.SELECT; options: Record<string, unknown> };

export enum ValidationStatus {
  HIDE,
  INCOMPLETE,
  FAIL,
  WARN,
  PASS,
}

type ValidationResult = {
  status: ValidationStatus;
  message: string;
};

export const validation = {
  hide: (message = '') => ({
    status: ValidationStatus.HIDE as const,
    message,
  }),
  incomplete: (message = '') => ({
    status: ValidationStatus.INCOMPLETE as const,
    message,
  }),
  fail: (message = '') => ({
    status: ValidationStatus.FAIL as const,
    message,
  }),
  warn: (message = '') => ({
    status: ValidationStatus.WARN as const,
    message,
  }),
  pass: (message = '') => ({
    status: ValidationStatus.PASS as const,
    message,
  }),
};

export type FieldContext<ValueType, ContextType> = {
  readonly type: FieldType;
  readonly context: ContextType;
  readonly transform: (
    value: ValueType,
    field: FieldContext<ValueType, ContextType>
  ) => ValueType;
  readonly transformInputValue: (
    inputValue: any,
    field: FieldContext<ValueType, ContextType>
  ) => any;
  readonly fromInputValue: (
    inputValue: any,
    field: FieldContext<ValueType, ContextType>
  ) => ValueType | undefined;
};

export type FormField<ValueType, ContextType> = FieldContext<
  ValueType,
  ContextType
> & {
  readonly label: string;
  readonly value: ValueType | undefined;
  readonly inputValue: any;
  readonly status: ValidationStatus;
  readonly message: string;
  readonly passing: boolean;
  readonly completed: boolean;
};

// `siblings` holds every field's normalized value, so a validator can depend
// on any other field regardless of declaration order. Validation state is
// not available there: only values are.
type FieldOptions<ValueType, ContextType, SiblingsType> = {
  label: ((context: ContextType) => string) | string;
  validate: (
    value: ValueType | undefined,
    context: ContextType,
    siblings: SiblingsType
  ) => ValidationResult;
  transform: FieldContext<ValueType, ContextType>['transform'];
  transformInputValue: FieldContext<
    ValueType,
    ContextType
  >['transformInputValue'];
  fromInputValue: FieldContext<ValueType, ContextType>['fromInputValue'];
  toInputValue: (value: ValueType | undefined, fieldType: FieldType) => any;
};

// Stored values can arrive unnormalized (e.g. the web app keeps a cleared
// input as ''); coerce anything that isn't a real answer to undefined so
// validators can rely on `undefined` meaning "not answered".
function normalizeValue(value: any, fieldType: FieldType) {
  switch (fieldType.type) {
    case FieldTypes.INTEGER:
    case FieldTypes.DECIMAL: {
      if (
        value === null ||
        (typeof value === 'string' && value.trim() === '')
      ) {
        return undefined;
      }
      const num = typeof value === 'string' ? Number(value.trim()) : value;
      return typeof num === 'number' && Number.isFinite(num) ? num : undefined;
    }
    case FieldTypes.SELECT:
      return Object.values(fieldType.options).includes(value)
        ? value
        : undefined;
    case FieldTypes.BOOLEAN:
      return value === null ? undefined : value;
  }
}

const defaultFieldOptions: FieldOptions<any, any, any> = {
  label: '',
  validate: () => validation.pass(),
  transform: (value) => value,
  transformInputValue: (inputValue, { type }) => {
    switch (type.type) {
      case FieldTypes.INTEGER:
        return (inputValue as string).trim().replace(/[^0-9-]/g, '');
      case FieldTypes.DECIMAL:
        return (inputValue as string).trim().replace(/[^0-9.-]/g, '');
      case FieldTypes.BOOLEAN:
        return !!inputValue;
      case FieldTypes.SELECT:
        return inputValue;
    }
  },
  fromInputValue: (inputValue, field) => {
    const value = normalizeValue(
      field.transformInputValue(inputValue, field),
      field.type
    );
    return value === undefined ? undefined : field.transform(value, field);
  },
  toInputValue: (value, fieldType) => {
    switch (fieldType.type) {
      case FieldTypes.INTEGER:
      case FieldTypes.DECIMAL:
        return isNaN(value as number) ? '' : (value as number).toString();
      case FieldTypes.BOOLEAN:
        return value;
      case FieldTypes.SELECT:
        return Object.values(fieldType.options).includes(value)
          ? value
          : undefined;
    }
  },
};

type FormFieldDef<ValueType, ContextType, SiblingsType> = {
  normalize: (value: unknown) => ValueType | undefined;
  build: (
    value: ValueType | undefined,
    context: ContextType,
    siblings: SiblingsType,
    inputValue: unknown
  ) => FormField<ValueType, ContextType>;
};

function createField<ValueType, ContextType, SiblingsType>(
  type: FieldType,
  params: Partial<FieldOptions<ValueType, ContextType, SiblingsType>>
): FormFieldDef<ValueType, ContextType, SiblingsType> {
  const p: FieldOptions<ValueType, ContextType, SiblingsType> = {
    ...defaultFieldOptions,
    ...params,
  };

  return {
    normalize: (value) => normalizeValue(value, type),

    build(value, context, siblings, inputValue) {
      const fieldContext: FieldContext<ValueType, ContextType> = {
        type,
        context,
        transform: p.transform,
        transformInputValue: p.transformInputValue,
        fromInputValue: p.fromInputValue,
      };
      const { status, message } = p.validate(value, context, siblings);

      return {
        ...fieldContext,
        label: typeof p.label === 'string' ? p.label : p.label(context),
        value,
        inputValue:
          inputValue === undefined
            ? p.toInputValue(value, type)
            : p.transformInputValue(inputValue, fieldContext),
        status,
        message,
        passing:
          status === ValidationStatus.PASS ||
          status === ValidationStatus.WARN ||
          status === ValidationStatus.HIDE,
        completed: status !== ValidationStatus.INCOMPLETE,
      };
    },
  };
}

export function createFieldCreator<
  ContextType,
  SiblingsType = Record<string, unknown>,
>() {
  return {
    integer: <T = number>(
      params: Partial<FieldOptions<T, ContextType, SiblingsType>>
    ) =>
      createField<T, ContextType, SiblingsType>(
        { type: FieldTypes.INTEGER },
        params
      ),

    decimal: <T = number>(
      params: Partial<FieldOptions<T, ContextType, SiblingsType>>
    ) =>
      createField<T, ContextType, SiblingsType>(
        { type: FieldTypes.DECIMAL },
        params
      ),

    boolean: <T = boolean>(
      params: Partial<FieldOptions<T, ContextType, SiblingsType>>
    ) =>
      createField<T, ContextType, SiblingsType>(
        { type: FieldTypes.BOOLEAN },
        params
      ),

    select: <const OptionsType extends Record<string, unknown>>(
      options: OptionsType,
      params: Partial<
        FieldOptions<OptionsType[keyof OptionsType], ContextType, SiblingsType>
      >
    ) =>
      createField<OptionsType[keyof OptionsType], ContextType, SiblingsType>(
        { type: FieldTypes.SELECT, options },
        params
      ),
  };
}

type FieldsDefs = Record<string, FormFieldDef<any, any, any>>;

type ValuesType<F extends FieldsDefs> = Partial<{
  [K in keyof F]: F[K] extends FormFieldDef<infer V, any, any> ? V : never;
}>;

type FieldsObjType<F extends FieldsDefs> = {
  [K in keyof F]: F[K] extends FormFieldDef<infer V, infer C, any>
    ? FormField<V, C>
    : never;
};

type FormContextType<F extends FieldsDefs> = {
  [K in keyof F]: F[K] extends FormFieldDef<any, infer C, any> ? C : never;
}[keyof F];

export type Form<FieldsType> = {
  readonly fields: FieldsType;
  readonly completed: boolean;
  readonly passing: boolean;
};

export function createForm<F extends FieldsDefs>(fields: F) {
  return {
    // The subset of `values` this form understands, normalized; unanswered
    // and unusable values are omitted.
    normalize(values: Record<string, unknown>): ValuesType<F> {
      const normalized: Record<string, unknown> = {};
      for (const [key, def] of Object.entries(fields)) {
        const value = def.normalize(values[key]);
        if (value !== undefined) normalized[key] = value;
      }
      return normalized as ValuesType<F>;
    },

    init(
      values: ValuesType<F>,
      context: FormContextType<F>,
      inputValues?: Record<string, unknown>
    ): Form<FieldsObjType<F>> {
      const siblings = this.normalize(
        values as Record<string, unknown>
      ) as Record<string, unknown>;

      const built: Record<string, FormField<any, any>> = {};
      for (const [key, def] of Object.entries(fields)) {
        built[key] = def.build(
          siblings[key],
          context,
          siblings,
          inputValues?.[key]
        );
      }

      const all = Object.values(built);
      return {
        fields: built as FieldsObjType<F>,
        completed: all.every((field) => field.completed),
        passing: all.every((field) => field.passing),
      };
    },
  };
}
